import type React from 'react';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import type { ContentData } from 'app/ui/components/molecules/content-item-areas-wrapper';

import Typography from 'app/ui/components/atoms/typography';
import GroupBreakdown from 'app/ui/components/molecules/whiteboard-slide/group-breakdown';

import SlideTimerContainer from 'app/ui/components/molecules/slide-timer/container';
import { useAppState } from 'state';

type AudienceLessonSectionBreakdownProps = {
  sectionData: ContentData;
  isSmallVariant?: boolean;
  isOverviewMode?: boolean;
};

const BreakdownWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme: { spacing } }) => spacing(8)};
`;

const ContentWrapper = styled.div<{ $isSmallVariant?: boolean }>`
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  max-width: calc(100% - ${({ theme }) => theme.spacing(8)});
  overflow: hidden;

  ${({ theme: { media } }) => `${media.large(
    () => `
        flex-wrap: wrap;
    `,
  )}
  `};
`;

const Zone = styled.div<{ $isSmallVariant?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4.5)};

  ${({ theme: { media }, $isSmallVariant }) => `${media.large(
    () => `
        width: ${$isSmallVariant ? '100%' : '50%'};
    `,
  )}
  `};
`;

const BaseSectionHeader = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: 2px;
`;

const SectionHeader = styled(BaseSectionHeader)`
  margin-top: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.focoltone};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border-top: 2px solid ${({ theme }) => theme.colors.focoltone};
  border-bottom: 2px solid ${({ theme }) => theme.colors.focoltone};
`;

const SectionTitle = styled(BaseSectionHeader)`
  background: transparent;
  padding: 0;
  ${({ theme: { media } }) => `${media.large(
    () => `
      width: 50%;
    `,
  )}
  `};
`;

const SectionContentWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  background: transparent;
  padding: ${({ theme }) => `${theme.spacing(3)} 0`};
  border-top: 2px solid ${({ theme }) => theme.colors.focoltone};
  border-bottom: 2px solid ${({ theme }) => theme.colors.focoltone};
  border-radius: 2px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${({ theme: { media } }) => `${media.large(
    () => `
      width: 50%;
    `,
  )}
  `};
`;

const SectionContent = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
  line-height: 1.25;
`;

const AudienceLessonSectionBreakdown: React.FC<AudienceLessonSectionBreakdownProps> = ({
  sectionData,
  isSmallVariant,
  isOverviewMode,
}) => {
  const theme = useTheme();
  const isTimerEnabled = useAppState((state) => state.timer.isTimerEnabled);

  const applySmallVariant = isSmallVariant || isOverviewMode;

  const textVariant = applySmallVariant ? 'body3' : 'whiteboard1';

  const timerId = useMemo(() => `section-${sectionData.id}-timer`, [sectionData]);

  return (
    <>
      <BreakdownWrapper>
        {isOverviewMode ? (
          <SectionHeader variant="body3" color={theme.colors.black}>
            <span>{sectionData.title}</span>
            <span>{sectionData.additionalInfo}</span>
          </SectionHeader>
        ) : (
          <>
            <SectionTitle variant="body3" color={theme.colors.white}>
              {sectionData.title} - {sectionData.additionalInfo}
            </SectionTitle>
            {sectionData?.content &&
              typeof sectionData.content === 'string' &&
              sectionData.content.trim().length > 0 && (
                <SectionContentWrapper>
                  <SectionContent variant={textVariant} color={theme.colors.focoltone}>
                    {sectionData.content}
                  </SectionContent>
                </SectionContentWrapper>
              )}
          </>
        )}

        <ContentWrapper $isSmallVariant={applySmallVariant}>
          {sectionData?.groups?.map((group, groupIndex) => (
            <Zone key={`zone-${group.id}`} $isSmallVariant={applySmallVariant}>
              <GroupBreakdown
                group={group}
                index={groupIndex + 1}
                key={`group-${group.id}`}
                isSingleGroup={(sectionData?.groups?.length || 0) <= 1}
                isSmallVariant={applySmallVariant}
                isOverviewMode={isOverviewMode}
              />
            </Zone>
          ))}
        </ContentWrapper>
      </BreakdownWrapper>

      {!isOverviewMode && isTimerEnabled && (
        <SlideTimerContainer timerId={timerId} sectionData={sectionData} endSeconds={sectionData.duration || 0} />
      )}
    </>
  );
};

export default AudienceLessonSectionBreakdown;
